import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";

const CartDrawer = () => {
  const { drawerOpen, setDrawerOpen, items, updateQty, removeItem, totals } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#F9F8F6] border-l border-[#E5E5E0]" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl text-[#2C3E2D] flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> Your Basket
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="mt-16 text-center px-6" data-testid="cart-empty">
            <div className="mx-auto h-20 w-20 rounded-full bg-[#F0EFEA] flex items-center justify-center mb-4">
              <ShoppingBag className="h-8 w-8 text-[#81B29A]" strokeWidth={1.5} />
            </div>
            <p className="text-[#2C3E2D] font-medium">Your basket is empty</p>
            <p className="text-sm text-[#6B7B6C] mt-1">Add farm-fresh produce or a warm meal to get started.</p>
            <Button
              onClick={() => { setDrawerOpen(false); navigate("/shop"); }}
              className="btn-primary rounded-full mt-6 px-6"
              data-testid="cart-empty-shop-btn"
            >Browse products</Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-col h-[calc(100vh-160px)]">
            <div className="flex-1 overflow-y-auto no-scrollbar space-y-3 pr-1" data-testid="cart-items-list">
              {items.map((item) => (
                <div key={item.product_id} className="flex gap-3 rounded-xl border border-[#E5E5E0] bg-white p-3" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.image_url} alt={item.name} className="h-16 w-16 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm text-[#2C3E2D] leading-tight">{item.name}</div>
                        <div className="text-xs text-[#6B7B6C] mt-0.5">{item.unit}</div>
                      </div>
                      <button onClick={() => removeItem(item.product_id)} className="text-[#6B7B6C] hover:text-red-500" data-testid={`cart-remove-${item.product_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-full border border-[#E5E5E0]">
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="p-1.5 hover:bg-[#F0EFEA] rounded-l-full"
                          data-testid={`cart-dec-${item.product_id}`}
                        ><Minus className="h-3.5 w-3.5" /></button>
                        <span className="px-3 text-sm font-medium" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          className="p-1.5 hover:bg-[#F0EFEA] rounded-r-full"
                          data-testid={`cart-inc-${item.product_id}`}
                        ><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="text-sm font-semibold text-[#2C3E2D]">₹{(item.price * item.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t border-[#E5E5E0] pt-4 space-y-2 text-sm">
              <Row label="Subtotal" value={`₹${totals.subtotal.toFixed(2)}`} testid="cart-subtotal" />
              <Row label="Delivery" value={totals.delivery === 0 ? "Free" : `₹${totals.delivery.toFixed(2)}`} testid="cart-delivery" />
              <Row label="Tax (5%)" value={`₹${totals.tax.toFixed(2)}`} testid="cart-tax" />
              <div className="flex justify-between items-center pt-2 border-t border-[#E5E5E0]">
                <span className="font-heading text-lg text-[#2C3E2D]">Total</span>
                <span className="font-heading text-lg font-semibold text-[#2C3E2D]" data-testid="cart-total">₹{totals.total.toFixed(2)}</span>
              </div>
              <Button
                onClick={goCheckout}
                className="btn-primary w-full rounded-full h-11 mt-2"
                data-testid="cart-checkout-btn"
              >Proceed to Checkout</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between items-center text-[#6B7B6C]">
    <span>{label}</span>
    <span className="text-[#2C3E2D] font-medium" data-testid={testid}>{value}</span>
  </div>
);

export default CartDrawer;
